/**
 * Guest session management for non-authenticated users
 * Handles subscription state for users who don't want to register
 */

import { generateDeviceFingerprint, validateDeviceFingerprint } from './deviceFingerprint';
import { getFromStorage, setInStorage, removeFromStorage } from './storage';
import { supabase } from '../lib/supabase/client';

export interface GuestSession {
  id: string;
  email?: string;
  emailVerified: boolean;
  subscriptionStatus: 'none' | 'pending' | 'active' | 'expired';
  subscriptionExpiry?: string;
  paddleSubscriptionId?: string;
  paddleCustomerId?: string;
  deviceFingerprint: string;
  createdAt: string;
  lastAccessAt: string;
}

const GUEST_SESSION_KEY = 'guestSession';

/**
 * Generates a unique guest session ID
 */
const generateGuestSessionId = (): string => {
  return `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Creates a new guest session
 * @param email Optional email for cross-device access
 * @returns The created guest session
 */
export const createGuestSession = async (email?: string): Promise<GuestSession> => {
  try {
    const deviceFingerprint = generateDeviceFingerprint();
    const sessionId = generateGuestSessionId();
    const now = new Date().toISOString();

    const guestSession: GuestSession = {
      id: sessionId,
      email,
      emailVerified: false,
      subscriptionStatus: 'none',
      deviceFingerprint,
      createdAt: now,
      lastAccessAt: now
    };

    // Save to localStorage
    setInStorage(GUEST_SESSION_KEY, JSON.stringify(guestSession));

    // Save to Supabase
    const { error } = await supabase
      .from('guest_sessions')
      .insert({
        id: sessionId,
        email,
        email_verified: false,
        subscription_status: 'none',
        device_fingerprint: deviceFingerprint,
        created_at: now,
        last_access_at: now
      });

    if (error) throw error;

    console.log('Guest session created in Supabase:', sessionId);
    return guestSession;
  } catch (error) {
    console.error('Error creating guest session in Supabase:', error);
    throw new Error('Failed to create guest session');
  }
};

/**
 * Gets the current guest session from localStorage
 * @returns The guest session or null if not found
 */
export const getGuestSession = (): GuestSession | null => {
  try {
    const sessionData = getFromStorage(GUEST_SESSION_KEY);
    if (!sessionData) return null;

    const session: GuestSession = JSON.parse(sessionData);
    
    // Validate device fingerprint
    if (!validateDeviceFingerprint(session.deviceFingerprint)) {
      console.log('Device fingerprint mismatch, clearing guest session');
      clearGuestSession();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error getting guest session:', error);
    clearGuestSession();
    return null;
  }
};

/**
 * Gets guest session from Supabase by ID
 * @param sessionId The session ID to retrieve
 * @returns The guest session or null if not found
 */
export const getGuestSessionFromFirestore = async (sessionId: string): Promise<GuestSession | null> => {
  try {
    const { data, error } = await supabase
      .from('guest_sessions')
      .select('*')
      .eq('id', sessionId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      id: data.id,
      email: data.email,
      emailVerified: data.email_verified,
      subscriptionStatus: data.subscription_status,
      subscriptionExpiry: data.subscription_expiry,
      paddleSubscriptionId: data.paddle_subscription_id,
      paddleCustomerId: data.paddle_customer_id,
      deviceFingerprint: data.device_fingerprint,
      createdAt: data.created_at,
      lastAccessAt: data.last_access_at
    };
  } catch (error) {
    console.error('Error getting guest session from Supabase:', error);
    return null;
  }
};

/**
 * Updates the guest session
 * @param updates Partial guest session data to update
 * @returns The updated guest session
 */
export const updateGuestSession = async (updates: Partial<GuestSession>): Promise<GuestSession | null> => {
  try {
    const currentSession = getGuestSession();
    if (!currentSession) {
      throw new Error('No guest session found to update');
    }

    const updatedSession: GuestSession = {
      ...currentSession,
      ...updates,
      lastAccessAt: new Date().toISOString()
    };

    // Update localStorage
    setInStorage(GUEST_SESSION_KEY, JSON.stringify(updatedSession));

    // Update Supabase mapping updates
    const dbUpdates: any = {};
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.emailVerified !== undefined) dbUpdates.email_verified = updates.emailVerified;
    if (updates.subscriptionStatus !== undefined) dbUpdates.subscription_status = updates.subscriptionStatus;
    if (updates.subscriptionExpiry !== undefined) dbUpdates.subscription_expiry = updates.subscriptionExpiry;
    if (updates.paddleSubscriptionId !== undefined) dbUpdates.paddle_subscription_id = updates.paddleSubscriptionId;
    if (updates.paddleCustomerId !== undefined) dbUpdates.paddle_customer_id = updates.paddleCustomerId;
    
    dbUpdates.last_access_at = new Date().toISOString();

    const { error } = await supabase
      .from('guest_sessions')
      .update(dbUpdates)
      .eq('id', currentSession.id);

    if (error) throw error;

    console.log('Guest session updated in Supabase:', currentSession.id);
    return updatedSession;
  } catch (error) {
    console.error('Error updating guest session in Supabase:', error);
    throw new Error('Failed to update guest session');
  }
};

/**
 * Clears the guest session from localStorage
 */
export const clearGuestSession = (): void => {
  removeFromStorage(GUEST_SESSION_KEY);
  console.log('Guest session cleared from localStorage');
};

/**
 * Checks if the current guest session has an active subscription
 * @returns True if the guest has an active subscription
 */
export const hasActiveGuestSubscription = (): boolean => {
  const session = getGuestSession();
  if (!session || session.subscriptionStatus !== 'active') {
    return false;
  }

  // Check expiry date if present
  if (session.subscriptionExpiry) {
    const expiry = new Date(session.subscriptionExpiry);
    const now = new Date();
    return expiry > now;
  }

  return true;
};

/**
 * Activates guest subscription after successful payment
 * @param paddleSubscriptionId The Paddle subscription ID
 * @param paddleCustomerId The Paddle customer ID
 * @param expiryDate The subscription expiry date
 */
export const activateGuestSubscription = async (
  paddleSubscriptionId: string,
  paddleCustomerId: string,
  expiryDate?: Date
): Promise<GuestSession | null> => {
  try {
    const updates: Partial<GuestSession> = {
      subscriptionStatus: 'active',
      paddleSubscriptionId,
      paddleCustomerId,
      subscriptionExpiry: expiryDate?.toISOString()
    };

    return await updateGuestSession(updates);
  } catch (error) {
    console.error('Error activating guest subscription:', error);
    throw new Error('Failed to activate guest subscription');
  }
};

/**
 * Links a guest session to a permanent Supabase user account
 * @param userId The permanent user ID
 * @param guestSessionId The guest session ID
 */
export const linkGuestSessionToUser = async (userId: string, guestSessionId: string): Promise<void> => {
  try {
    const guestSession = await getGuestSessionFromFirestore(guestSessionId);
    if (!guestSession) {
      throw new Error('Guest session not found');
    }

    console.log(`[GuestSession] Linking guest session ${guestSessionId} to user ${userId}`);
    
    // Transfer subscription data to user profile in Supabase
    if (guestSession.subscriptionStatus === 'active') {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          subscription_status: 'premium',
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);
        
      if (error) throw error;
      
      localStorage.setItem('subscriptionStatus', 'active');
      console.log(`[GuestSession] Upgraded user ${userId} to premium via guest session`);
    }

    // Clear local guest session
    clearGuestSession();
  } catch (error) {
    console.error('Error linking guest session to user:', error);
    throw new Error('Failed to link guest session to user account');
  }
};

/**
 * Finds guest sessions by email
 * @param email The email to search for
 * @returns Array of matching guest sessions
 */
export const findGuestSessionsByEmail = async (email: string): Promise<GuestSession[]> => {
  try {
    const { data, error } = await supabase
      .from('guest_sessions')
      .select('*')
      .eq('email', email);

    if (error) throw error;
    if (!data) return [];

    return data.map(doc => ({
      id: doc.id,
      email: doc.email,
      emailVerified: doc.email_verified,
      subscriptionStatus: doc.subscription_status,
      subscriptionExpiry: doc.subscription_expiry,
      paddleSubscriptionId: doc.paddle_subscription_id,
      paddleCustomerId: doc.paddle_customer_id,
      deviceFingerprint: doc.device_fingerprint,
      createdAt: doc.created_at,
      lastAccessAt: doc.last_access_at
    }));
  } catch (error) {
    console.error('Error finding guest sessions by email:', error);
    return [];
  }
};
